import { useState, useCallback, useRef } from 'react';
import assignedToChallanService from '../services/assignedToChallanService';
import i18n from '../i18n';

const useAssignedToChallan = () => {
    // Pending (live backlog) view
    const [pendingItems, setPendingItems] = useState([]);
    const [pendingSummary, setPendingSummary] = useState(null);
    const [pendingTotal, setPendingTotal] = useState(0);
    const [serverTime, setServerTime] = useState(null);

    // Duration (historical) view
    const [duration, setDuration] = useState(null);

    const [fetching, setFetching] = useState(false);
    const [error, setError] = useState(null);

    const pendingAbortRef = useRef(null);
    const durationAbortRef = useRef(null);

    const fetchPending = useCallback(async (params = {}) => {
        if (pendingAbortRef.current) pendingAbortRef.current.abort();
        const controller = new AbortController();
        pendingAbortRef.current = controller;

        setFetching(true);
        setError(null);
        try {
            const result = await assignedToChallanService.getPending(params, { signal: controller.signal });
            if (result.isCanceled) return;
            if (result.success) {
                const raw = result.data;
                setPendingItems(raw?.items ?? []);
                setPendingSummary(raw?.summary ?? null);
                setPendingTotal(raw?.total ?? 0);
                setServerTime(raw?.server_time ?? null);
            } else {
                setError(result.error);
                setPendingItems([]);
                setPendingSummary(null);
            }
            return result;
        } catch (err) {
            setError(err.message || i18n.t('hookErrors.assignedToChallan.fetchPendingBacklog'));
        } finally {
            if (pendingAbortRef.current === controller) {
                setFetching(false);
                pendingAbortRef.current = null;
            }
        }
    }, []);

    const fetchDuration = useCallback(async (params = {}) => {
        if (durationAbortRef.current) durationAbortRef.current.abort();
        const controller = new AbortController();
        durationAbortRef.current = controller;

        setFetching(true);
        setError(null);
        try {
            const result = await assignedToChallanService.getDuration(params, { signal: controller.signal });
            if (result.isCanceled) return;
            if (result.success) {
                setDuration(result.data ?? null);
            } else {
                setError(result.error);
                setDuration(null);
            }
            return result;
        } catch (err) {
            setError(err.message || i18n.t('hookErrors.assignedToChallan.fetchDurationStats'));
        } finally {
            if (durationAbortRef.current === controller) {
                setFetching(false);
                durationAbortRef.current = null;
            }
        }
    }, []);

    return {
        pendingItems,
        pendingSummary,
        pendingTotal,
        serverTime,
        duration,
        fetching,
        error,
        fetchPending,
        fetchDuration,
    };
};

export default useAssignedToChallan;
